import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { AppProvider } from "@/context/AppContext";
import { ProtectedRoute, AuthRoute, PublicRoute, GuestRoute, ViewOnlyRoute } from "@/components/RouteGuards";
import Navigation from "@/components/Navigation";
import HomePage from "@/pages/HomePage";
import EntryDetailPage from "@/pages/EntryDetailPage";
import EntryFormPage from "@/pages/EntryFormPage";
import MapPage from "@/pages/MapPage";
import TagsPage from "@/pages/TagsPage";
import StatisticsPage from "@/pages/StatisticsPage";
import SpeciesGalleryPage from "@/pages/SpeciesGalleryPage";
import CalendarPage from "@/pages/CalendarPage";
import PhotoGalleryPage from "@/pages/PhotoGalleryPage";
import PrintViewPage from "@/pages/PrintViewPage";
import MarketplacePage from "@/pages/MarketplacePage";
import EBirdPage from "@/pages/EBirdPage";
import OfflineMapPage from "@/pages/OfflineMapPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import LandingPage from "@/pages/LandingPage";
import PricingPage from "@/pages/PricingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import AdminPage from "@/pages/AdminPage";
import AccountPage from "@/pages/AccountPage";
import PublicGalleryPage from "@/pages/PublicGalleryPage";

// Leaflet setup (side-effect import)
import "@/config/leaflet";

// Attach Bearer token to all requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-refresh expired access tokens
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve());
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/login") &&
      !originalRequest.url?.includes("/auth/register") &&
      !originalRequest.url?.includes("/auth/refresh") &&
      !originalRequest.url?.includes("/admin/")
    ) {
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        return Promise.reject(error);
      }
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve: () => resolve(axios(originalRequest)), reject });
        });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, { refresh_token: refreshToken });
        localStorage.setItem("access_token", data.access_token);
        processQueue(null);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

const AppShell = ({ children }) => (
  <>
    <Navigation />
    {children}
  </>
);

function App() {
  // Global image protection: block right-click on images and save shortcuts
  useEffect(() => {
    const handleContextMenu = (e) => {
      if (e.target.tagName === "IMG") {
        e.preventDefault();
      }
    };
    const handleKeyDown = (e) => {
      // Block Ctrl+S (save), Ctrl+Shift+I (dev tools), Ctrl+U (view source)
      if (e.ctrlKey && (e.key === "s" || e.key === "S" || e.key === "u" || e.key === "U")) {
        e.preventDefault();
      }
    };
    const handleDragStart = (e) => {
      if (e.target.tagName === "IMG") {
        e.preventDefault();
      }
    };
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("dragstart", handleDragStart);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("dragstart", handleDragStart);
    };
  }, []);

  return (
    <AuthProvider>
      <AppProvider>
        <div className="App min-h-screen transition-colors duration-300" style={{backgroundColor: 'transparent'}}>
          <Toaster position="top-right" richColors />
          <BrowserRouter>
            <Routes>
              {/* Public routes */}
              <Route path="/welcome" element={<PublicRoute><LandingPage /></PublicRoute>} />
              <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
              <Route path="/signup" element={<GuestRoute><SignupPage /></GuestRoute>} />

              {/* Auth-only routes (no payment required) */}
              <Route path="/pricing" element={<AuthRoute><PricingPage /></AuthRoute>} />
              <Route path="/payment-success" element={<AuthRoute><PaymentSuccessPage /></AuthRoute>} />

              {/* View-only routes (anyone can view, no login required) */}
              <Route path="/" element={<ViewOnlyRoute><AppShell><HomePage /></AppShell></ViewOnlyRoute>} />
              <Route path="/calendar" element={<ViewOnlyRoute><AppShell><CalendarPage /></AppShell></ViewOnlyRoute>} />
              <Route path="/entry/:id" element={<ViewOnlyRoute><AppShell><EntryDetailPage /></AppShell></ViewOnlyRoute>} />
              <Route path="/gallery" element={<ViewOnlyRoute><AppShell><PhotoGalleryPage /></AppShell></ViewOnlyRoute>} />
              <Route path="/species" element={<ViewOnlyRoute><AppShell><SpeciesGalleryPage /></AppShell></ViewOnlyRoute>} />
              <Route path="/stats" element={<ViewOnlyRoute><AppShell><StatisticsPage /></AppShell></ViewOnlyRoute>} />
              <Route path="/map" element={<ViewOnlyRoute><AppShell><MapPage /></AppShell></ViewOnlyRoute>} />
              <Route path="/tags" element={<ViewOnlyRoute><AppShell><TagsPage /></AppShell></ViewOnlyRoute>} />
              <Route path="/marketplace" element={<ViewOnlyRoute><AppShell><MarketplacePage /></AppShell></ViewOnlyRoute>} />
              <Route path="/ebird" element={<ViewOnlyRoute><AppShell><EBirdPage /></AppShell></ViewOnlyRoute>} />
              <Route path="/offline-maps" element={<ViewOnlyRoute><AppShell><OfflineMapPage /></AppShell></ViewOnlyRoute>} />

              {/* Protected routes (login + payment required — editing only) */}
              <Route path="/entry/new" element={<ProtectedRoute><AppShell><EntryFormPage /></AppShell></ProtectedRoute>} />
              <Route path="/entry/:id/edit" element={<ProtectedRoute><AppShell><EntryFormPage /></AppShell></ProtectedRoute>} />
              <Route path="/print" element={<ProtectedRoute><AppShell><PrintViewPage /></AppShell></ProtectedRoute>} />
              <Route path="/account" element={<ProtectedRoute><AppShell><AccountPage /></AppShell></ProtectedRoute>} />

              {/* Admin panel (password-gated, no auth required) */}
              <Route path="/admin" element={<AdminPage />} />

              {/* Public gallery (no auth required) */}
              <Route path="/public" element={<PublicGalleryPage />} />
              <Route path="/public/:entryId" element={<PublicGalleryPage />} />

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/public" replace />} />
            </Routes>
          </BrowserRouter>
        </div>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
